import {
  END_USER_DEDUCTION_SETUP_STATES,
  END_USER_ONBOARDING_STATES,
} from 'services/endUser/endUserJourneyState.data';
import { normalizeEndUserProfileState } from 'services/endUser/endUserRouteAccess';

export type ContactSupportFormPayload = {
  name: string;
  email: string;
  message: string;
};

export type SupportContactRequestBody = {
  name: string;
  email: string;
  message: string;
};

/**
 * POST /eu/support-tickets body for profile_enhanced,
 * deduction_setup_created, and deduction_setup_verified.
 */
export type SupportTicketsRequestBody = {
  subject: string;
  description: string;
};

/** Profile journey state from auth/dashboard (may be unset). */
export type ContactSupportProfileStateInput = string | null | undefined;

/** Request body for either support-tickets or legacy support/contact. */
export type ContactSupportRequestBody = SupportContactRequestBody | SupportTicketsRequestBody;

const SUPPORT_TICKETS_SUBJECT_MAX_LENGTH = 120;
const SUPPORT_TICKETS_DEFAULT_SUBJECT = 'Get in touch';

const SUPPORT_TICKETS_PROFILE_STATES = new Set<string>([
  END_USER_ONBOARDING_STATES.profileEnhanced,
  END_USER_DEDUCTION_SETUP_STATES.created,
  END_USER_DEDUCTION_SETUP_STATES.verified,
]);

/**
 * Get-in-touch / contact support path:
 * - `profile_enhanced`, `deduction_setup_created`, or `deduction_setup_verified`
 *   → POST /eu/support-tickets `{ subject, description }`
 * - otherwise → POST /eu/{euId}/support/contact
 */
export function shouldUseEuSupportTicketsEndpoint(
  profileState: ContactSupportProfileStateInput
): boolean {
  const key = normalizeEndUserProfileState(profileState)?.replace(/_/g, '-') ?? null;
  return key !== null && SUPPORT_TICKETS_PROFILE_STATES.has(key);
}

export function resolveContactSupportRequestPath(
  euId: string,
  profileState: ContactSupportProfileStateInput
): string {
  if (shouldUseEuSupportTicketsEndpoint(profileState)) {
    return '/eu/support-tickets';
  }

  return `/eu/${encodeURIComponent(euId)}/support/contact`;
}

export function buildSupportContactRequestBody(
  payload: ContactSupportFormPayload
): SupportContactRequestBody {
  return {
    name: payload.name.trim(),
    email: payload.email.trim(),
    message: payload.message.trim(),
  };
}

/**
 * Maps Get-in-touch message → support-tickets `{ subject, description }`.
 * Subject = first line of message (truncated). Description = full message.
 */
export function buildSupportTicketsRequestBody(
  payload: ContactSupportFormPayload
): SupportTicketsRequestBody {
  const description = payload.message.trim();
  const firstLine = description.split(/\r?\n/)[0]?.trim() ?? '';
  const subject =
    firstLine.slice(0, SUPPORT_TICKETS_SUBJECT_MAX_LENGTH) || SUPPORT_TICKETS_DEFAULT_SUBJECT;

  return { subject, description };
}

export function buildContactSupportRequestBody(
  payload: ContactSupportFormPayload,
  profileState: ContactSupportProfileStateInput
): ContactSupportRequestBody {
  if (shouldUseEuSupportTicketsEndpoint(profileState)) {
    return buildSupportTicketsRequestBody(payload);
  }

  return buildSupportContactRequestBody(payload);
}

/** True when the API indicates the bearer/session can no longer be used. */
export function isContactSupportSessionExpiredError(
  status: number | undefined,
  responseData: { message?: string } | undefined
): boolean {
  if (status === 401 || status === 403) {
    return true;
  }

  const message = responseData?.message?.trim().toLowerCase() ?? '';
  if (!message) {
    return false;
  }

  return message.includes('token') && message.includes('expired');
}
